import { useMutation } from '@tanstack/react-query'
import { fetchMFCRedirectionData } from '../../../repository/misc'
import { useEffect, useRef, useState } from 'react'
import { captureException } from '@sentry/react'
import { goToPortfolioDashboard, requestAppDataFromFlutter, sendbase64ImageUpload, trackEvent } from '../../../utils/puAppBridge'
import { useFlutterInAppWebView } from '../../../hooks/useFlutterInAppWebView'
import { EventNames } from '../../../types/misc.types'
import { isDevEnvironment } from '../../../utils/utils'

const MAX_RETRIES = 6
const API_TIMEOUT_MS = 12000
const FLUTTER_READY_TIMEOUT_MS = 5000

export const MfcErrorCode = {
    APP_BRIDGE_UPLOAD_FAILED: 'MFC_ERR_001',
    API_FAILED_AFTER_RETRIES: 'MFC_ERR_002'
} as const

export type MfcPageError = { code: string; message?: string }

function isFlutterInAppWebViewAvailable(): boolean {
    if (isDevEnvironment()) return true
    return !!(typeof window !== 'undefined' && window?.flutter_inappwebview && typeof window?.flutter_inappwebview?.callHandler === 'function')
}

type UseMFCRedirectionOptions = {
    componentTag: string
    uploadOnSuccess: boolean
}

export function useMFCRedirection({ componentTag, uploadOnSuccess }: UseMFCRedirectionOptions) {
    const retryCountRef = useRef(0)
    const timeoutIdRef = useRef<NodeJS.Timeout>()
    const flutterTimeoutIdRef = useRef<NodeJS.Timeout>()
    const hasTriggeredInitialMutateRef = useRef(false)
    const ctaDataRef = useRef<any | null>(null)
    const [error, setError] = useState<MfcPageError | null>(null)
    const { isFlutterReady } = useFlutterInAppWebView()

    const { mutate, isPending, isError, isSuccess } = useMutation({
        mutationFn: fetchMFCRedirectionData,
        onSuccess: (data) => {
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
            const ctaData = data?.cta ?? data?.data?.cta
            if (ctaData) {
                ctaDataRef.current = ctaData
                if (uploadOnSuccess) {
                    if (!isFlutterInAppWebViewAvailable()) {
                        captureException(new Error('sendbase64ImageUpload failed: Flutter InAppWebView not available'), {
                            tags: { component: componentTag, mfcErrorCode: MfcErrorCode.APP_BRIDGE_UPLOAD_FAILED },
                            extra: { url: window.location.href }
                        })
                        setError({ code: MfcErrorCode.APP_BRIDGE_UPLOAD_FAILED })
                        return
                    }
                    sendbase64ImageUpload(JSON.stringify(ctaData))
                }
            } else if (uploadOnSuccess) {
                captureException(new Error('no cta from response'), {
                    tags: { component: componentTag, errorType: 'api_failure' },
                    extra: { retryCount: retryCountRef.current, url: window.location.href }
                })
            }
        },
        onError: (err: any) => {
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
            const httpStatus = err?.response?.status ?? 'unknown'
            const errorMessage = err?.response?.data?.message ?? err?.message ?? 'Unknown error'

            if (retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current += 1
                mutate()
            } else {
                captureException(new Error(`API failed ${httpStatus}: ${errorMessage}`), {
                    tags: { component: componentTag, mfcErrorCode: MfcErrorCode.API_FAILED_AFTER_RETRIES },
                    extra: {
                        retryCount: retryCountRef.current,
                        httpStatus,
                        errorMessage,
                        url: window.location.href
                    }
                })
                setError({
                    code: MfcErrorCode.API_FAILED_AFTER_RETRIES,
                    message: 'Please try again.'
                })
            }
        }
    })

    useEffect(() => {
        requestAppDataFromFlutter()
        trackEvent({
            eventName: EventNames.ScreenLoaded,
            payload: { eventId: 'mfc_redirection_reached', screenType: 'MFC_REDIRECTION_PAGE' }
        })
        trackEvent({
            eventName: EventNames.UserClicked,
            payload: { eventId: 'mfc_redirection_called', screenType: 'MFC_REDIRECTION_PAGE' }
        })

        timeoutIdRef.current = setTimeout(() => {
            if (!isSuccess && !isError) {
                captureException(new Error('API call timeout'), {
                    tags: { component: componentTag, errorType: 'timeout' },
                    extra: { timeoutMs: API_TIMEOUT_MS, retryCount: retryCountRef.current, url: window.location.href }
                })
            }
        }, API_TIMEOUT_MS)

        flutterTimeoutIdRef.current = setTimeout(() => {
            if (!hasTriggeredInitialMutateRef.current) {
                hasTriggeredInitialMutateRef.current = true
                mutate()
            }
        }, FLUTTER_READY_TIMEOUT_MS)

        return () => {
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
            if (flutterTimeoutIdRef.current) clearTimeout(flutterTimeoutIdRef.current)
        }
    }, [])

    useEffect(() => {
        if (!isFlutterReady) return
        if (hasTriggeredInitialMutateRef.current) return
        hasTriggeredInitialMutateRef.current = true
        if (flutterTimeoutIdRef.current) {
            clearTimeout(flutterTimeoutIdRef.current)
            flutterTimeoutIdRef.current = undefined
        }
        requestAppDataFromFlutter()
        mutate()
    }, [isFlutterReady])

    const onTryAgain = () => {
        setError(null)
        retryCountRef.current = 0
        mutate()
    }

    const onContinueClick = () => {
        const ctaData = ctaDataRef.current
        if (!ctaData) return
        if (!isFlutterInAppWebViewAvailable()) {
            captureException(new Error('sendbase64ImageUpload failed: Flutter InAppWebView not available'), {
                tags: { component: componentTag, mfcErrorCode: MfcErrorCode.APP_BRIDGE_UPLOAD_FAILED },
                extra: { url: window.location.href }
            })
            setError({ code: MfcErrorCode.APP_BRIDGE_UPLOAD_FAILED })
            return
        }
        try {
            sendbase64ImageUpload(JSON.stringify(ctaData))
        } catch (err) {
            captureException(new Error('sendbase64ImageUpload failed on Continue click'), {
                tags: { component: componentTag, mfcErrorCode: MfcErrorCode.APP_BRIDGE_UPLOAD_FAILED },
                extra: { url: window.location.href, originalError: (err as Error)?.message }
            })
            setError({ code: MfcErrorCode.APP_BRIDGE_UPLOAD_FAILED })
        }
    }

    return {
        error,
        isPending,
        isSuccess,
        onTryAgain,
        onGoToPortfolio: goToPortfolioDashboard,
        ctaDataRef,
        onContinueClick
    }
}
